import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const PurchaseTable = ({ 
  data, 
  type, 
  onSort, 
  onFilter, 
  onAction,
  searchTerm: externalSearchTerm = '',
  onSearchChange,
  statusFilter: externalStatusFilter = '',
  onStatusChange
}) => {
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [internalSearchTerm, setInternalSearchTerm] = useState('');
  const [internalStatusFilter, setInternalStatusFilter] = useState('');
  const [filteredData, setFilteredData] = useState(data);
  const [selectedItems, setSelectedItems] = useState([]);

  // Use external search/filter if provided, otherwise use internal state
  const searchTerm = externalSearchTerm || internalSearchTerm;
  const statusFilter = externalStatusFilter || internalStatusFilter;

  // Apply filters whenever data, search, or status changes
  useEffect(() => {
    let filtered = [...data];

    // Apply search filter
    if (searchTerm?.trim()) {
      filtered = filtered?.filter(item => {
        const searchFields = getSearchableFields(item, type);
        return searchFields?.some(field => 
          field?.toLowerCase()?.includes(searchTerm?.toLowerCase())
        );
      });
    }

    // Apply status filter
    if (statusFilter) {
      filtered = filtered?.filter(item => item?.status === statusFilter);
    }

    setFilteredData(filtered);
  }, [data, searchTerm, statusFilter, type]);

  const getSearchableFields = (item, type) => {
    switch (type) {
      case 'vendors':
        return [
          item?.vendor_name || '',
          item?.email || '',
          item?.phone || '',
          item?.company_name || '',
          item?.vendor_number || ''
        ];
      case 'expenses':
        return [
          item?.expense_number || '',
          item?.description || '',
          item?.vendor?.vendor_name || '',
          item?.reference_number || '',
          item?.category || ''
        ];
      case 'recurring-expenses':
        return [
          item?.template_name || '',
          item?.template_number || '',
          item?.description || '',
          item?.vendor?.vendor_name || '',
          item?.category || ''
        ];
      case 'bills': case 'recurring-bills':
        return [
          item?.bill_number || item?.template_number || '',
          item?.vendor?.vendor_name || '',
          item?.reference_number || ''
        ];
      case 'payment-made':
        return [
          item?.payment_reference || '',
          item?.reference_number || '',
          item?.description || ''
        ];
      case 'vendor-credit':
        return [
          item?.credit_number || '',
          item?.reference_number || '',
          item?.description || '',
          item?.vendor?.vendor_name || ''
        ];
      default:
        return [];
    }
  };

  // Add bulk selection handlers
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedItems(filteredData?.map(item => item?.id));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (itemId, checked) => {
    if (checked) {
      setSelectedItems([...selectedItems, itemId]);
    } else {
      setSelectedItems(selectedItems?.filter(id => id !== itemId));
    }
  };

  const handleBulkDelete = () => {
    if (selectedItems?.length > 0) {
      onAction('bulk-delete', selectedItems);
      setSelectedItems([]);
    }
  };

  const handleSort = (field) => {
    const direction = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(direction);
    
    // Sort the filtered data
    const sorted = [...filteredData]?.sort((a, b) => {
      const aValue = a?.[field];
      const bValue = b?.[field];
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return direction === 'asc' 
          ? aValue?.localeCompare(bValue)
          : bValue?.localeCompare(aValue);
      }
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      return 0;
    });
    
    setFilteredData(sorted);
    onSort(field, direction);
  };

  const handleSearchChange = (e) => {
    const value = e?.target?.value;
    if (onSearchChange) {
      onSearchChange(value);
    } else {
      setInternalSearchTerm(value);
    }
  };

  const handleStatusChange = (value) => {
    if (onStatusChange) {
      onStatusChange(value);
    } else {
      setInternalStatusFilter(value);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'approved':
        return 'bg-success/10 text-success border-success/20';
      case 'paid':
        return 'bg-primary/10 text-primary border-primary/20';
      case 'overdue':
        return 'bg-error/10 text-error border-error/20';
      case 'active':
        return 'bg-success/10 text-success border-success/20';
      case 'inactive':
        return 'bg-muted text-muted-foreground border-border';
      case 'blocked':
        return 'bg-error/10 text-error border-error/20';
      case 'completed':
        return 'bg-primary/10 text-primary border-primary/20';
      case 'processing':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'failed':
        return 'bg-error/10 text-error border-error/20';
      case 'rejected':
        return 'bg-error/10 text-error border-error/20';
      case 'paused':
        return 'bg-muted text-muted-foreground border-border';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getStatusOptions = () => {
    const baseOptions = [{ value: '', label: 'All Status' }];
    
    switch (type) {
      case 'vendors':
        return [...baseOptions, 
          { value: 'active', label: 'Active' },
          { value: 'inactive', label: 'Inactive' },
          { value: 'suspended', label: 'Suspended' },
          { value: 'archived', label: 'Archived' }
        ];
      case 'expenses':
        return [...baseOptions,
          { value: 'draft', label: 'Draft' },
          { value: 'pending_approval', label: 'Pending Approval' },
          { value: 'approved', label: 'Approved' },
          { value: 'rejected', label: 'Rejected' },
          { value: 'paid', label: 'Paid' },
          { value: 'reimbursed', label: 'Reimbursed' }
        ];
      case 'recurring-expenses': case 'recurring-bills':
        return [...baseOptions,
          { value: 'draft', label: 'Draft' },
          { value: 'active', label: 'Active' },
          { value: 'paused', label: 'Paused' },
          { value: 'cancelled', label: 'Cancelled' },
          { value: 'completed', label: 'Completed' }
        ];
      case 'bills':
        return [...baseOptions,
          { value: 'draft', label: 'Draft' },
          { value: 'pending_approval', label: 'Pending Approval' },
          { value: 'approved', label: 'Approved' },
          { value: 'paid', label: 'Paid' },
          { value: 'overdue', label: 'Overdue' },
          { value: 'cancelled', label: 'Cancelled' },
          { value: 'rejected', label: 'Rejected' }
        ];
      case 'payment-made':
        return [...baseOptions,
          { value: 'pending', label: 'Pending' },
          { value: 'cleared', label: 'Cleared' },
          { value: 'bounced', label: 'Bounced' },
          { value: 'cancelled', label: 'Cancelled' },
          { value: 'refunded', label: 'Refunded' }
        ];
      case 'vendor-credit':
        return [...baseOptions,
          { value: 'draft', label: 'Draft' },
          { value: 'issued', label: 'Issued' },
          { value: 'applied', label: 'Applied' },
          { value: 'cancelled', label: 'Cancelled' }
        ];
      default:
        return baseOptions;
    }
  };

  const renderTableHeader = () => {
    if (type === 'vendors') {
      return (
        <tr className="border-b border-border">
          <th className="w-12 px-4 py-3">
            <input
              type="checkbox"
              checked={selectedItems?.length === filteredData?.length && filteredData?.length > 0}
              onChange={(e) => handleSelectAll(e?.target?.checked)}
              className="rounded border-border"
            />
          </th>
          <th className="text-left py-3 px-4 font-medium text-muted-foreground">
            <button
              onClick={() => handleSort('vendor_name')}
              className="flex items-center space-x-1 hover:text-foreground"
            >
              <span>Vendor</span>
              <Icon name="ArrowUpDown" size={14} />
            </button>
          </th>
          <th className="text-left py-3 px-4 font-medium text-muted-foreground">Contact</th>
          <th className="text-left py-3 px-4 font-medium text-muted-foreground">
            <button
              onClick={() => handleSort('credit_limit')}
              className="flex items-center space-x-1 hover:text-foreground"
            >
              <span>Credit Limit</span>
              <Icon name="ArrowUpDown" size={14} />
            </button>
          </th>
          <th className="text-left py-3 px-4 font-medium text-muted-foreground">Status</th>
          <th className="text-right py-3 px-4 font-medium text-muted-foreground">Actions</th>
        </tr>
      );
    } else if (type === 'expenses' || type === 'recurring-expenses') {
      return (
        <tr className="border-b border-border">
          <th className="w-12 px-4 py-3">
            <input
              type="checkbox"
              checked={selectedItems?.length === filteredData?.length && filteredData?.length > 0}
              onChange={(e) => handleSelectAll(e?.target?.checked)}
              className="rounded border-border"
            />
          </th>
          <th className="text-left py-3 px-4 font-medium text-muted-foreground">
            <button
              onClick={() => handleSort('expense_number')}
              className="flex items-center space-x-1 hover:text-foreground"
            >
              <span>{type === 'expenses' ? 'Expense #' : 'Template'}</span>
              <Icon name="ArrowUpDown" size={14} />
            </button>
          </th>
          <th className="text-left py-3 px-4 font-medium text-muted-foreground">Description</th>
          <th className="text-left py-3 px-4 font-medium text-muted-foreground">Vendor</th>
          <th className="text-left py-3 px-4 font-medium text-muted-foreground">
            <button
              onClick={() => handleSort('amount')}
              className="flex items-center space-x-1 hover:text-foreground"
            >
              <span>Amount</span>
              <Icon name="ArrowUpDown" size={14} />
            </button>
          </th>
          <th className="text-left py-3 px-4 font-medium text-muted-foreground">Category</th>
          <th className="text-left py-3 px-4 font-medium text-muted-foreground">Status</th>
          <th className="text-right py-3 px-4 font-medium text-muted-foreground">Actions</th>
        </tr>
      );
    } else if (type === 'bills' || type === 'recurring-bills') {
      return (
        <tr className="border-b border-border">
          <th className="w-12 px-4 py-3">
            <input
              type="checkbox"
              checked={selectedItems?.length === filteredData?.length && filteredData?.length > 0}
              onChange={(e) => handleSelectAll(e?.target?.checked)}
              className="rounded border-border"
            />
          </th>
          <th className="text-left py-3 px-4 font-medium text-muted-foreground">
            <button
              onClick={() => handleSort('bill_number')}
              className="flex items-center space-x-1 hover:text-foreground"
            >
              <span>{type === 'bills' ? 'Bill #' : 'Template'}</span>
              <Icon name="ArrowUpDown" size={14} />
            </button>
          </th>
          <th className="text-left py-3 px-4 font-medium text-muted-foreground">Vendor</th>
          <th className="text-left py-3 px-4 font-medium text-muted-foreground">
            <button
              onClick={() => handleSort(type === 'bills' ? 'total_amount' : 'amount')}
              className="flex items-center space-x-1 hover:text-foreground"
            >
              <span>Amount</span>
              <Icon name="ArrowUpDown" size={14} />
            </button>
          </th>
          <th className="text-left py-3 px-4 font-medium text-muted-foreground">
            <button
              onClick={() => handleSort(type === 'bills' ? 'due_date' : 'next_billing_date')}
              className="flex items-center space-x-1 hover:text-foreground"
            >
              <span>{type === 'bills' ? 'Due Date' : 'Next Due Date'}</span>
              <Icon name="ArrowUpDown" size={14} />
            </button>
          </th>
          <th className="text-left py-3 px-4 font-medium text-muted-foreground">Status</th>
          <th className="text-right py-3 px-4 font-medium text-muted-foreground">Actions</th>
        </tr>
      );
    } else {
      return (
        <tr className="border-b border-border">
          <th className="w-12 px-4 py-3">
            <input
              type="checkbox"
              checked={selectedItems?.length === filteredData?.length && filteredData?.length > 0}
              onChange={(e) => handleSelectAll(e?.target?.checked)}
              className="rounded border-border"
            />
          </th>
          <th className="text-left py-3 px-4 font-medium text-muted-foreground">
            <button
              onClick={() => handleSort('payment_reference')}
              className="flex items-center space-x-1 hover:text-foreground"
            >
              <span>Reference</span>
              <Icon name="ArrowUpDown" size={14} />
            </button>
          </th>
          <th className="text-left py-3 px-4 font-medium text-muted-foreground">Description</th>
          <th className="text-left py-3 px-4 font-medium text-muted-foreground">
            <button
              onClick={() => handleSort('amount_received')}
              className="flex items-center space-x-1 hover:text-foreground"
            >
              <span>Amount</span>
              <Icon name="ArrowUpDown" size={14} />
            </button>
          </th>
          <th className="text-left py-3 px-4 font-medium text-muted-foreground">Date</th>
          <th className="text-left py-3 px-4 font-medium text-muted-foreground">Status</th>
          <th className="text-right py-3 px-4 font-medium text-muted-foreground">Actions</th>
        </tr>
      );
    }
  };

  const renderTableRow = (item) => {
    if (type === 'vendors') {
      return (
        <tr key={item?.id} className="border-b border-border hover:bg-muted/30 transition-colors">
          <td className="px-4 py-3">
            <input
              type="checkbox"
              checked={selectedItems?.includes(item?.id)}
              onChange={(e) => handleSelectItem(item?.id, e?.target?.checked)}
              className="rounded border-border"
            />
          </td>
          <td className="py-3 px-4">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Building2" size={16} className="text-primary" />
              </div>
              <div>
                <p className="font-medium text-foreground">{item?.vendor_name}</p>
                <p className="text-sm text-muted-foreground">{item?.vendor_number}</p>
              </div>
            </div>
          </td>
          <td className="py-3 px-4">
            <p className="text-sm text-foreground">{item?.email}</p>
            <p className="text-sm text-muted-foreground">{item?.phone}</p>
          </td>
          <td className="py-3 px-4">
            <p className="font-medium text-foreground">${(item?.credit_limit || 0)?.toLocaleString()}</p>
          </td>
          <td className="py-3 px-4">
            <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(item?.status)}`}>
              {item?.status}
            </span>
          </td>
          <td className="py-3 px-4 text-right">
            <div className="flex items-center justify-end space-x-2">
              <Button variant="ghost" size="sm" onClick={() => onAction('edit', item)}>
                <Icon name="Edit" size={16} />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onAction('view', item)}>
                <Icon name="Eye" size={16} />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => onAction('delete', item)}
                className="text-error hover:text-error hover:bg-error/10"
              >
                <Icon name="Trash2" size={16} />
              </Button>
            </div>
          </td>
        </tr>
      );
    } else if (type === 'expenses' || type === 'recurring-expenses') {
      return (
        <tr key={item?.id} className="border-b border-border hover:bg-muted/30 transition-colors">
          <td className="px-4 py-3">
            <input
              type="checkbox"
              checked={selectedItems?.includes(item?.id)}
              onChange={(e) => handleSelectItem(item?.id, e?.target?.checked)}
              className="rounded border-border"
            />
          </td>
          <td className="py-3 px-4">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-orange-500/10 rounded-lg flex items-center justify-center">
                <Icon name={type === 'expenses' ? 'Receipt' : 'RefreshCw'} size={16} className="text-orange-500" />
              </div>
              <div>
                <p className="font-medium text-foreground">{item?.expense_number || item?.template_number}</p>
                <p className="text-sm text-muted-foreground">{item?.template_name && `Template: ${item?.template_name}`}</p>
              </div>
            </div>
          </td>
          <td className="py-3 px-4">
            <p className="text-foreground">{item?.description}</p>
          </td>
          <td className="py-3 px-4">
            <p className="text-foreground">{item?.vendor?.vendor_name || 'No Vendor'}</p>
          </td>
          <td className="py-3 px-4">
            <p className="font-medium text-foreground">${(item?.amount || 0)?.toLocaleString()}</p>
          </td>
         <td className="py-3 px-4">
            <p className="text-foreground">
              {item?.account?.account_name || '-'}
            </p>
          </td>
          <td className="py-3 px-4">
            <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(item?.status)}`}>
              {item?.status}
            </span>
          </td>
          <td className="py-3 px-4 text-right">
            <div className="flex items-center justify-end space-x-2">
              {item?.status === 'pending_approval' && type === 'expenses' && (
                <Button variant="outline" size="sm" onClick={() => onAction('approve', item)}>
                  Approve
                </Button>
              )}
              {item?.status === 'paused' && type === 'recurring-expenses' && (
                <Button variant="outline" size="sm" onClick={() => onAction('resume', item)}>
                  Resume
                </Button>
              )}
              {item?.status === 'active' && type === 'recurring-expenses' && (
                <Button variant="outline" size="sm" onClick={() => onAction('pause', item)}>
                  Pause
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={() => onAction('duplicate', item)}>
                <Icon name="Copy" size={16} />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onAction('edit', item)}>
                <Icon name="Edit" size={16} />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => onAction('delete', item)}
                className="text-error hover:text-error hover:bg-error/10"
              >
                <Icon name="Trash2" size={16} />
              </Button>
            </div>
          </td>
        </tr>
      );
    } else if (type === 'bills' || type === 'recurring-bills') {
      return (
        <tr key={item?.id} className="border-b border-border hover:bg-muted/30 transition-colors">
          <td className="px-4 py-3">
            <input
              type="checkbox"
              checked={selectedItems?.includes(item?.id)}
              onChange={(e) => handleSelectItem(item?.id, e?.target?.checked)}
              className="rounded border-border"
            />
          </td>
          <td className="py-3 px-4">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-blue-500/10 rounded-lg flex items-center justify-center">
                <Icon name={type === 'bills' ? 'FileText' : 'RotateCcw'} size={16} className="text-blue-500" />
              </div>
              <div>
                <p className="font-medium text-foreground">{item?.bill_number || item?.template_number}</p>
                <p className="text-sm text-muted-foreground">{item?.template_name && `Template: ${item?.template_name}`}</p>
              </div>
            </div>
          </td>
          <td className="py-3 px-4">
            <p className="text-foreground">{item?.vendor?.vendor_name}</p>
          </td>
          <td className="py-3 px-4">
            <p className="font-medium text-foreground">${(item?.total_amount || item?.amount || 0)?.toLocaleString()}</p>
          </td>
          <td className="py-3 px-4">
            <p className="text-foreground">
              {new Date(item?.due_date || item?.next_billing_date)?.toLocaleDateString()}
            </p>
          </td>
          <td className="py-3 px-4">
            <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(item?.status)}`}>
              {item?.status}
            </span>
          </td>
          <td className="py-3 px-4 text-right">
            <div className="flex items-center justify-end space-x-2">
              {item?.status === 'pending_approval' && type === 'bills' && (
                <Button variant="outline" size="sm" onClick={() => onAction('approve', item)}>
                  Approve
                </Button>
              )}
              {item?.status === 'approved' && type === 'bills' && (
                <Button variant="outline" size="sm" onClick={() => onAction('pay', item)}>
                  Pay
                </Button>
              )}
              {item?.status === 'paused' && type === 'recurring-bills' && (
                <Button variant="outline" size="sm" onClick={() => onAction('resume', item)}>
                  Resume
                </Button>
              )}
              {item?.status === 'active' && type === 'recurring-bills' && (
                <Button variant="outline" size="sm" onClick={() => onAction('pause', item)}>
                  Pause
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={() => onAction('edit', item)}>
                <Icon name="Edit" size={16} />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => onAction('delete', item)}
                className="text-error hover:text-error hover:bg-error/10"
              >
                <Icon name="Trash2" size={16} />
              </Button>
            </div>
          </td>
        </tr>
      );
    } else if (type === 'vendor-credit') {
      return (
        <tr key={item?.id} className="border-b border-border hover:bg-muted/30 transition-colors">
          <td className="px-4 py-3">
            <input
              type="checkbox"
              checked={selectedItems?.includes(item?.id)}
              onChange={(e) => handleSelectItem(item?.id, e?.target?.checked)}
              className="rounded border-border"
            />
          </td>
          <td className="py-3 px-4">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-green-500/10 rounded-lg flex items-center justify-center">
                <Icon name="Banknote" size={16} className="text-green-500" />
              </div>
              <div>
                <p className="font-medium text-foreground">{item?.credit_number}</p>
                <p className="text-sm text-muted-foreground">{item?.reference_number}</p>
              </div>
            </div>
          </td>
          <td className="py-3 px-4">
            <p className="text-foreground">{item?.description || item?.reason}</p>
          </td>
          <td className="py-3 px-4">
            <p className="font-medium text-foreground">${(item?.amount || 0)?.toLocaleString()}</p>
            {item?.remaining_balance > 0 && (
              <p className="text-sm text-primary">Balance: ${item?.remaining_balance?.toLocaleString()}</p>
            )}
          </td>
          <td className="py-3 px-4">
            <p className="text-foreground">
              {new Date(item?.credit_date)?.toLocaleDateString()}
            </p>
          </td>
          <td className="py-3 px-4">
            <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(item?.status)}`}>
              {item?.status}
            </span>
          </td>
          <td className="py-3 px-4 text-right">
            <div className="flex items-center justify-end space-x-2">
              {item?.status === 'draft' && (
                <Button variant="outline" size="sm" onClick={() => onAction('approve', item)}>
                  Approve
                </Button>
              )}
              {item?.status === 'issued' && item?.remaining_balance > 0 && (
                <Button variant="outline" size="sm" onClick={() => onAction('apply', item)}>
                  Apply
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={() => onAction('edit', item)}>
                <Icon name="Edit" size={16} />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => onAction('delete', item)}
                className="text-error hover:text-error hover:bg-error/10"
              >
                <Icon name="Trash2" size={16} />
              </Button>
            </div>
          </td>
        </tr>
      );
    } else {
      return (
        <tr key={item?.id} className="border-b border-border hover:bg-muted/30 transition-colors">
          <td className="px-4 py-3">
            <input
              type="checkbox"
              checked={selectedItems?.includes(item?.id)}
              onChange={(e) => handleSelectItem(item?.id, e?.target?.checked)}
              className="rounded border-border"
            />
          </td>
          <td className="py-3 px-4">
            <p className="font-medium text-foreground">{item?.payment_reference}</p>
          </td>
          <td className="py-3 px-4">
            <p className="text-foreground">{item?.description}</p>
          </td>
          <td className="py-3 px-4">
            <p className="font-medium text-foreground">${(item?.amount_received || 0)?.toLocaleString()}</p>
          </td>
          <td className="py-3 px-4">
            <p className="text-foreground">
              {new Date(item?.payment_date)?.toLocaleDateString()}
            </p>
          </td>
          <td className="py-3 px-4">
            <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(item?.status)}`}>
              {item?.status}
            </span>
          </td>
          <td className="py-3 px-4 text-right">
            <div className="flex items-center justify-end space-x-2">
              <Button variant="ghost" size="sm" onClick={() => onAction('view', item)}>
                <Icon name="Eye" size={16} />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => onAction('delete', item)}
                className="text-error hover:text-error hover:bg-error/10"
              >
                <Icon name="Trash2" size={16} />
              </Button>
            </div>
          </td>
        </tr>
      );
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg">
      {/* Table Filters */}
      <div className="p-4 border-b border-border">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Input
              type="search"
              placeholder={`Search ${type}...`}
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full"
            />
          </div>
          <div className="w-full sm:w-48">
            <Select
              options={getStatusOptions()}
              value={statusFilter}
              onChange={handleStatusChange}
              placeholder="Filter by status"
            />
          </div>
        </div>
      </div>
      
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            {renderTableHeader()}
          </thead>
          <tbody>
            {filteredData?.map(renderTableRow)}
            {filteredData?.length === 0 && (
              <tr>
                <td colSpan="7" className="py-12 text-center">
                  <div className="flex flex-col items-center space-y-2">
                    <Icon name="Search" size={48} className="text-muted-foreground/30" />
                    <h3 className="text-lg font-medium text-foreground">No results found</h3>
                    <p className="text-muted-foreground">
                      {searchTerm || statusFilter 
                        ? "Try adjusting your search terms or filters" 
                        : `No ${type} available`}
                    </p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      {/* Bulk Actions Bar */}
      {selectedItems?.length > 0 && (
        <div className="border-t border-border bg-muted/30 px-4 py-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-foreground">
              {selectedItems?.length} item{selectedItems?.length > 1 ? 's' : ''} selected
            </span>
            <div className="flex items-center space-x-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleBulkDelete}
                className="text-error hover:text-error hover:bg-error/10 hover:border-error/20"
              >
                <Icon name="Trash2" size={14} className="mr-2" />
                Delete Selected
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setSelectedItems([])}
                className="text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={14} className="mr-2" />
                Clear Selection
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Table Footer */}
      <div className="p-4 border-t border-border">
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {filteredData?.length} of {data?.length} results
            {searchTerm && ` matching "${searchTerm}"`}
            {statusFilter && ` with status "${statusFilter}"`}
          </p>
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" disabled>
              <Icon name="ChevronLeft" size={16} />
              Previous
            </Button>
            <Button variant="outline" size="sm" disabled>
              Next
              <Icon name="ChevronRight" size={16} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseTable;